import type { OrganizationType } from "@prisma/client";
import { z } from "zod";
import type { Capability } from "@remi/enums/capability";
import { type PermissionRole, PermissionRoleValues } from "@remi/types/permissions";
import type { RegionRole } from "@remi/types/region";

import { expandCapabilitiesFromRoles } from "../role-capability-mapping";

// Public surface for converting a set of database-stored PermissionRoles into
// the runtime Capability[]. Composes the inheritance-graph traversal with the
// role-to-capability mapping. This is the canonical role→capability seam;
// production code should consume this via transformPersonPermissionAndRegionRoles
// at session load. Tests/scripts may call directly.
export const expandRolesToCapabilities = (roles: PermissionRole[]): Capability[] =>
  expandCapabilitiesFromRoles(getExhaustivePermissionRoles(roles));

export const getRolesWithCapability = (capability: Capability): PermissionRole[] =>
  PermissionRoleValues.filter((role) => expandRolesToCapabilities([role]).includes(capability));

const RemiPermissionRoleValues = [
  "REMI_OPS",
  "REMI_USER",
  "REMI_SALES",
  "REMI_SALES_ADMIN",
  "REMI_ADMIN",
  "REMI_AI_OPERATIONS",
  "REMI_DESIGNER",
  "REMI_ENGINEER",
  "IMPERSONATOR",
  "REMI_IMPERSONATION_AUDITOR",
  "REMI_FINANCIAL",
  "REMI_FINANCIAL_ADMIN",
  "REMI_REGIONAL_MANAGER",
  "REMI_DEADLINE_MANAGER",
  "REMI_CHANGE_ORDER_MANAGER",
  "REMI_WORK_ORDER_MANAGER",
  "REMI_DISPATCH",
  "REMI_CHANGE_ORDER_SPECIALIST",
  "REMI_ACCOUNT_MANAGEMENT_LEAD",
  "REMI_ACCOUNT_MANAGER",
  "REMI_QUOTING",
  "REMI_CLAIM_CAPTURE_TEAM",
  "REMI_MCP_USER",
  "REMI_MARKETING_WEBSITE",
  "AUTONOMOUS_AGENT",
  "PARTNER_DEVELOPER",
  "ESTIMATOR",
  "ESTIMATE_REVIEWER",
  "ESTIMATE_ADMIN",
] as const satisfies PermissionRole[];

export const ContractorPermissionRoleValues = [
  "CONTRACTOR_PRODUCTION",
  "CONTRACTOR_ADMIN",
] as const satisfies PermissionRole[];

export const ContractorPermissionRoleSchema = z.enum(ContractorPermissionRoleValues);
export const ContractorPermissionRole = ContractorPermissionRoleSchema.enum;
export type ContractorPermissionRole = (typeof ContractorPermissionRoleValues)[number];

export const ContractorPermissionRoleDisplayNames: Record<ContractorPermissionRole, string> = {
  CONTRACTOR_ADMIN: "Admin",
  CONTRACTOR_PRODUCTION: "Crew Member",
};

const BuyerPermissionRoleValues = ["BUYER_ADMIN", "INSURANCE_ADJUSTER"] as const satisfies PermissionRole[];

const HomeownerPermissionRoleValues = ["HOMEOWNER"] as const satisfies PermissionRole[];

export const OrganizationTypeToPermissionRoles: Record<OrganizationType, PermissionRole[]> = {
  CONTRACTOR: ContractorPermissionRoleValues,
  SOLAR_COMPANY: BuyerPermissionRoleValues,
  REMI_INTERNAL: RemiPermissionRoleValues,
  VENDOR: [],
  HOMEOWNER: HomeownerPermissionRoleValues,
  FUNDING: [],
};

const PermissionRoleInheritanceGraph: Record<PermissionRole, PermissionRole[]> = {
  INVALID: [],
  REMI_USER: [],
  REMI_OPS: ["REMI_USER"],
  REMI_SALES_ADMIN: ["REMI_ADMIN", "REMI_SALES"],
  REMI_SALES: ["REMI_USER"],
  REMI_ADMIN: ["REMI_USER", "REMI_SALES", "REMI_OPS", "INSURANCE_ADJUSTER", "ESTIMATE_REVIEWER", "REMI_QUOTING"],
  REMI_AI_OPERATIONS: [],
  REMI_AUTOMATIONS: ["REMI_ADMIN"],
  REMI_ENGINEER: [
    "REMI_ADMIN",
    "REMI_FINANCIAL_ADMIN",
    "REMI_SALES_ADMIN",
    "PARTNER_DEVELOPER",
    "INSURANCE_ADJUSTER",
    "REMI_QUOTE_ADMIN",
    "REMI_COMPLIANCE",
    "REMI_COMPLIANCE_ADMIN",
    "ESTIMATE_ADMIN",
    "REMI_MCP_USER",
    "REMI_CHANGE_ORDER_MANAGER",
    "REMI_WORK_ORDER_MANAGER",
    "REMI_DISPATCH",
  ],
  IMPERSONATOR: [],
  REMI_IMPERSONATION_AUDITOR: [],
  REMI_DESIGNER: ["REMI_USER"],
  REMI_COMPLIANCE: [],
  REMI_COMPLIANCE_ADMIN: ["REMI_COMPLIANCE"],
  CONTRACTOR_ADMIN: ["CONTRACTOR_PRODUCTION"],
  CONTRACTOR_PRODUCTION: [],
  NOMAD_INSPECTION: [],
  NOMAD_QA: [],
  NOMAD_CORRECTION: [],
  BUYER_ADMIN: [],
  REMI_REGIONAL_MANAGER: [],
  REMI_DEADLINE_MANAGER: [],
  REMI_PERMISSIONS_MANAGER: [],
  REMI_CHANGE_ORDER_MANAGER: [],
  REMI_WORK_ORDER_MANAGER: ["REMI_USER"],
  REMI_DISPATCH: ["REMI_USER"],
  REMI_CHANGE_ORDER_SPECIALIST: ["REMI_USER"],
  REMI_ACCOUNT_MANAGEMENT_LEAD: [],
  // Unrelated to REMI_ACCOUNT_MANAGEMENT_LEAD despite the name: LEAD is a transfer-only
  // role and does not confer account-manager access. Do not link them.
  REMI_ACCOUNT_MANAGER: ["REMI_USER"],
  REMI_QUOTING: ["REMI_USER"],
  REMI_CLAIM_CAPTURE_TEAM: ["REMI_USER"],
  REMI_MCP_USER: [],
  REMI_MARKETING_WEBSITE: [],
  // No inheritance by design: its capability set is an explicit REMI_OPS + REMI_USER union minus the
  // partner-message and outbound-SMS send capabilities, composed in RoleCapabilityMapping. Inheriting
  // REMI_USER would re-grant those sends, and the graph cannot subtract.
  AUTONOMOUS_AGENT: [],
  REMI_FINANCIAL_ADMIN: ["REMI_FINANCIAL", "REMI_ADMIN", "REMI_QUOTE_ADMIN"],
  REMI_QUOTE_ADMIN: ["REMI_ADMIN"],
  REMI_FINANCIAL: ["REMI_USER"],
  HOMEOWNER: [],
  PARTNER_DEVELOPER: [],
  INSURANCE_ADJUSTER: [],
  FIELD_WORKER: [],
  ESTIMATOR: [],
  ESTIMATE_REVIEWER: ["ESTIMATOR"],
  ESTIMATE_ADMIN: ["ESTIMATE_REVIEWER", "REMI_ADMIN"],
};

export const getExhaustivePermissionRoles = (roles: PermissionRole[]): PermissionRole[] => {
  return [
    ...new Set(
      roles.flatMap((role) => {
        const inheritedRoles = PermissionRoleInheritanceGraph[role];
        return [role, ...getExhaustivePermissionRoles(inheritedRoles)];
      }),
    ),
  ];
};

export const transformPersonPermissionAndRegionRoles = <
  TPerson extends {
    permissionRoles: { id: PermissionRole }[];
    regionRoles: { role: RegionRole }[];
  },
>(
  person: TPerson,
): Omit<TPerson, "permissionRoles" | "regionRoles" | "capabilities"> & {
  permissionRoles: PermissionRole[];
  regionRoles: RegionRole[];
  capabilities: Capability[];
} => {
  const expandedRoles = getExhaustivePermissionRoles(person.permissionRoles.map((role) => role.id));
  return {
    ...person,
    permissionRoles: expandedRoles,
    regionRoles: person.regionRoles.map((role) => role.role),
    capabilities: expandCapabilitiesFromRoles(expandedRoles),
  };
};

export const NON_EXISTENT_ID = "id_that_does_not_exist_r3M1_h4Ck";
export const NON_EXISTENT_NUMERIC_ID = -999999999999999;
